// Top.tsx
import React, { useState, useContext, useCallback,useEffect } from 'react';
import {
  signInWithPopup,
  GoogleAuthProvider,
  signInAnonymously,
  UserCredential,
  getAdditionalUserInfo
} from 'firebase/auth';
import { Menu, User, PlusCircle, Home, Search, Sparkles, Gift,BookOpenCheck } from 'lucide-react';
import { useParams,useNavigate ,useSearchParams} from 'react-router-dom';
import { useMyQuizSelection } from './hooks/useMyQuizSelection';


import { auth } from './firebase';
import { UserContext } from './services/user/LoginUserContext';
import { useUserProfile } from './hooks/useUserProfile';
import { useQuizManager } from './hooks/useQuizManager';

import LoginView from './components/common/LoginView';
import ProfileView from './components/profile/ProfileView';
import FindQuizzesView from './components/quiz/FindQuizzesView';
import CreateQuizView from './components/quiz/CreateQuizView';
import StreakBadgeWithModal from './components/quiz/StreakBadgeWithModal';
import GiftCodeEntryView from './components/quiz/GiftCodeEntryView';
import TodayChallengeQuizSection from './components/quiz/TodayChallengeQuizSection';
import AdsSection from './components/ads/AdsSection';
import QuizListSection from './components/quiz/QuizListSection';
import CustomDialog from './components/common/CustomDialog';
import LoadingView from './components/common/LoadingView';
import QuizView from './components/quiz/QuizView';
import TaskPlannerView from './components/task/TaskPlannerView';



import './Top.css';
import { logger } from './utils/logger';

import { addMyQuizSelection } from './services/myQuizSelectionService';

import { fetchQuiz} from './services/quizService';


// showMessage関数の型定義
type ShowMessageType = (
  message: string,
  onConfirm?: () => void,
  onCancel?: (() => void) | null,
  showCancel?: boolean
) => void;

// 型定義
interface HomeViewProps {
  reviewCount: number;
  startReviewQuiz: () => void;
  startQuiz: (id: string) => void;
  handlePageChange: (page: string) => void;
  userId: string;
  reviewItemsLoading: boolean;
}

const HomeView: React.FC<HomeViewProps> = ({
  reviewCount,
  startReviewQuiz,
  startQuiz,
  handlePageChange,
  userId,
  reviewItemsLoading
}) => {
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto flex flex-col space-y-6">
      <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-800 text-center mb-6 leading-tight">
        今日も<span className="text-transparent bg-clip-text bg-custom-gradient from-sky-500 to-cyan-400">賢く</span>なろう
      </h1>
      {reviewItemsLoading ? (
        <div className="text-center text-gray-600">復習データを読み込み中...</div>
      ) : (
        <TodayChallengeQuizSection reviewCount={reviewCount} onClick={startReviewQuiz} />
      )}
      <AdsSection userId={userId} />
      <QuizListSection startQuiz={startQuiz} handlePageChange={handlePageChange} userId={userId} />
    </div>
  );
};

interface NavProps {
  handlePageChange: (page: string) => void;
  userId: string;
}

const Nav: React.FC<NavProps> = ({ handlePageChange, userId }) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  return (
    <header className="bg-white/80 backdrop-blur-lg shadow-md sticky top-0 z-40">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex-shrink-0">
            <button onClick={() => handlePageChange('home')} className="flex items-center space-x-2">
              <div className="p-1 rounded-lg bg-custom-gradient from-sky-400 to-cyan-400">
                <Sparkles size={32} className="text-white transform -rotate-12" />
              </div>
              <span className="text-4xl font-extrabold text-gradient from-cyan-500 to-violet-500">QuizA</span>
            </button>
          </div>
          <div className="flex items-center">
            <StreakBadgeWithModal userId={userId} />
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-3 text-gray-600 hover:text-cyan-500">
              <Menu size={28} />
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="absolute top-20 left-0 w-full bg-white shadow-lg z-50 flex flex-col items-center space-y-2 py-4 animate-jump-in">
            <button onClick={() => { handlePageChange('home'); setMenuOpen(false); }} className="w-11/12 p-3 flex items-center font-bold hover:text-cyan-500"><Home className="mr-2" size={20} /> ホーム</button>
            <button onClick={() => { handlePageChange('createQuiz'); setMenuOpen(false); }} className="w-11/12 p-3 flex items-center font-bold hover:text-cyan-500"><PlusCircle className="mr-2" size={20} /> クイズ作成</button>
            <button onClick={() => { handlePageChange('findQuizzes'); setMenuOpen(false); }} className="w-11/12 p-3 flex items-center font-bold hover:text-cyan-500"><Search className="mr-2" size={20} /> クイズを探す</button>
            <button onClick={() => { handlePageChange('giftCodeEntry'); setMenuOpen(false); }} className="w-11/12 p-3 flex items-center font-bold hover:text-cyan-500"><Gift className="mr-2" size={20} /> ギフトコード</button>
            <button onClick={() => { handlePageChange('myPage'); setMenuOpen(false); }} className="w-11/12 p-3 flex items-center font-bold hover:text-cyan-500"><User className="mr-2" size={20} /> マイページ</button>
            <button onClick={() => { handlePageChange('taskPlanner'); setMenuOpen(false); }} className="w-11/12 p-3 flex items-center font-bold hover:text-cyan-500"><BookOpenCheck className="mr-2" size={20} />タスク管理 
              {/* 小さな「BETA」ラベルを追加 */}
              <span className="ml-2 px-1.5 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">
                  BETA
              </span></button>
          </div>
        )}
      </nav>
    </header>
  );
};

export default function Top(): React.ReactElement {
  // URLパラメータからquizIdを取得(クイズ直接呼び出し用)
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate(); //useNavigateフックを使用
  const [searchParams] = useSearchParams();

  const { user, loading: userContextLoading } = useContext(UserContext);

  //myQuizSelectionを取得し、読み込み状態も取得
  const { myQuizSelection, isLoading: myQuizLoading } = useMyQuizSelection(user?.uid || '');


  // URLにquizIdがある場合は初期ページを 'quiz' に設定し、ない場合は 'home' に設定
  const [currentPage, setCurrentPage] = useState<string>(quizId ? 'quiz' : 'home'); 
  
  const { profile, isLoading: userProfileKoading } = useUserProfile(user?.uid);
  const [dialogConfig, setDialogConfig] = useState<{
    message: string | null;
    onConfirm?: () => void;
    onCancel?: () => void;
    showCancel?: boolean;
  }>({
    message: null,
    onConfirm: undefined,
    onCancel: undefined,
    showCancel: true
  });

  const {
    activeQuiz,
    reviewCount,
    startQuiz,
    startReviewQuiz,
    onQuizComplete,
    quizToEditId,
    setQuizToEditId,
    setActiveQuiz,
    reviewItemsLoading
  } = useQuizManager({ setCurrentPage });

  const showMessage = useCallback(
    (message: string, onConfirm: () => void = () => { }, onCancel: () => void = () => { }, showCancel: boolean = true) => {
      setDialogConfig({
        message,
        onConfirm: () => { onConfirm(); setDialogConfig({ message: null }); },
        onCancel: () => { onCancel(); setDialogConfig({ message: null }); },
        showCancel
      });
    }, []);
// ユーザーがロードされ、かつURLにクイズIDがある場合にクイズを自動で開始する
useEffect(() => {
  const isReady = user && !userContextLoading && !userProfileKoading && !activeQuiz && quizId && !myQuizLoading;

  if (isReady) {
    
    // 【修正5】アクセス制御（リスト存在チェック/非表示チェック）
    const isQuizInList = myQuizSelection?.quizSummaries.some(q => q.id === quizId);
    const isQuizHidden = myQuizSelection?.hiddenQuizIdList?.includes(quizId);
    
    if (!isQuizInList || isQuizHidden) {
      let errorMessage = isQuizHidden ? 
          'このクイズは非表示設定になっています。リストから再表示してください。' : 
          '対象のクイズが見つかりませんでした。ホーム画面に戻ります。';

      showMessage(
        errorMessage,
        () => {
          setCurrentPage('home'); 
          // 【重要】URLクリーンアップ（リロード時の無限ループ防止）
          navigate('/Top', { replace: true }); 
        },
        null, 
        false
      );
      return; 
    }
    
    // 【修正6】問題数制限の取得
    const limitParam = searchParams.get('limit');
    const questionLimit = limitParam ? parseInt(limitParam, 10) : undefined; // 数値に変換し、無効ならundefined

    // 内部状態を 'quiz' にし、クイズを開始
    setCurrentPage('quiz');
    // 【修正7】startQuiz に問題数制限を渡す
    startQuiz(quizId, showMessage, questionLimit); 
  }
  
  // 【修正8】依存配列の追加・修正
}, [user, userContextLoading, userProfileKoading, quizId, activeQuiz, myQuizLoading, myQuizSelection, navigate, searchParams, startQuiz]); // startQuizも依存に追加

useEffect(() => {
    // activeQuizがセットされており（クイズが開始された）、かつURLに quizId パラメータが残っている場合
    if (activeQuiz && quizId) {
      // replace: 履歴を残さずに /Top へ URLを置き換える
      navigate('/Top', { replace: true });
    }
  }, [activeQuiz, quizId, navigate]); // 依存配列に含める



  if (userContextLoading || !user || reviewItemsLoading || userProfileKoading) {
    return <LoadingView />;
  }

  const handlePageChange = (page: string) => {
    setActiveQuiz(null);
    if (page !== 'createQuiz') {
      setQuizToEditId(null);
    }

    // クイズ作成ページへの遷移前にニックネームをチェック
    if (page === 'createQuiz' && !profile.nickname) {
      showMessage(
        '問題作成するにはニックネームの登録が必要です',
        () => {
                // OKボタンが押されたらマイページに遷移
          setCurrentPage('myPage');
          window.scrollTo(0, 0);
        },
            () => {}, // キャンセルボタンは表示しないので空関数を渡す
            false     // キャンセルボタンを非表示にする
      );
        return; // ここで処理を中断し、'createQuiz' へのページ遷移を防ぐ
    }

    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const renderPage = (): React.ReactElement => {
    if (!user) return 
    <LoginView
    onGoogleLogin={async () => {
      try {
        const result: UserCredential = await signInWithPopup(
          auth,
          new GoogleAuthProvider()
        );
        const info = getAdditionalUserInfo(result);
        const isNewUser = info?.isNewUser;

        logger.info("isNewUser:" + isNewUser);
        logger.info("info:" + info);
        
        if (isNewUser) {
          const initialQuizIds = [
            'rszBbfTtRV88aY45Y3cU'
            // 今後追加するクイズIDをここに列挙
            // 'anotherQuizId',
            // 'moreQuizId'
          ];

          for (const quizId of initialQuizIds) {
            const quiz = await fetchQuiz(quizId);
            if (quiz) {
              await addMyQuizSelection(result.user.uid, quiz);
            }
          }
          logger.log('新規ユーザー: 初回クイズを追加しました');
        }
      } catch (error) {
        logger.error('Google ログイン失敗:', error);
      }
    }}
          onAnonymousLogin={() => signInAnonymously(auth).then(() => {})}
    />;

    // ----- 初回ニックネーム登録の必須は一旦なくす -----
    // ニックネームがない場合に強制的にプロフィールページに飛ばすロジックを削除
    // if (!profile.nickname && currentPage !== 'myPage') {
    //   return <ProfileView userId={user.uid} handlePageChange={handlePageChange} showMessage={showMessage} />;
    // }

    switch (currentPage) {
      case 'home':
        return <HomeView reviewCount={reviewCount} startReviewQuiz={() => startReviewQuiz(showMessage)} startQuiz={(id) => startQuiz(id, showMessage)} handlePageChange={handlePageChange} userId={user.uid} reviewItemsLoading={reviewItemsLoading} />;
        case 'quiz':
          return activeQuiz ? (
            <QuizView quiz={activeQuiz} onQuizComplete={(quiz, answers) => onQuizComplete(quiz, answers, handlePageChange)} showMessage={showMessage} /> 
          ) : (
            // 👇 【修正】activeQuiz が null の場合（クイズロード中）は LoadingView を表示
            <LoadingView />
          );
      case 'createQuiz':
        return <CreateQuizView handlePageChange={handlePageChange} showMessage={showMessage} quizToEditId={quizToEditId} onQuizUpdated={() => { setQuizToEditId(null); handlePageChange('findQuizzes'); }} />;
      case 'myPage':
        return <ProfileView userId={user.uid} handlePageChange={handlePageChange} showMessage={showMessage} />;
      case 'findQuizzes':
        return <FindQuizzesView userId={user.uid} handlePageChange={handlePageChange} showMessage={showMessage} startQuiz={(id) => startQuiz(id, showMessage)} handleEditQuizId={(quizId) => { setQuizToEditId(quizId); handlePageChange('createQuiz'); }} />;
      case 'giftCodeEntry':
        return <GiftCodeEntryView userId={user.uid} handlePageChange={handlePageChange} />;
      case 'taskPlanner':
        return <TaskPlannerView  />;
      default:
        return <HomeView reviewCount={reviewCount} startReviewQuiz={() => startReviewQuiz(showMessage)} startQuiz={(id) => startQuiz(id, showMessage)} handlePageChange={handlePageChange} userId={user.uid} reviewItemsLoading={reviewItemsLoading} />;
    }
  };

  return (
    <div className="bg-sky-50 min-h-screen font-pop">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=M+PLUS+Rounded+1c:wght@400;700;800&display=swap');
        .font-pop { font-family: 'M PLUS Rounded 1c', sans-serif; }
        @keyframes jump-in { 0% { transform: scale(0.5); opacity: 0; } 80% { transform: scale(1.05); } 100% { transform: scale(1); opacity: 1; } }
        .animate-jump-in { animation: jump-in 0.3s ease-out forwards; }
      `}</style>
      <CustomDialog message={dialogConfig.message} onConfirm={dialogConfig.onConfirm} onCancel={dialogConfig.onCancel} showCancel={dialogConfig.showCancel} />
      <Nav handlePageChange={handlePageChange} userId={user.uid} />
      <main className="container mx-auto py-6 sm:py-8">
        {renderPage()}
      </main>
    </div>
  );
}
